// ── GIS / distance utilities ─────────────────────────────────────────────────
// Shared across StormAtlasRadarPanel, ORAOverlayCard, and any future components.

// Haversine great-circle distance in metres between two [lng, lat] points.
export function haversineMeters(a, b) {
  const R = 6_371_000;
  const φ1 = (a[1] * Math.PI) / 180;
  const φ2 = (b[1] * Math.PI) / 180;
  const Δφ = ((b[1] - a[1]) * Math.PI) / 180;
  const Δλ = ((b[0] - a[0]) * Math.PI) / 180;
  const s   = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// Haversine great-circle distance in kilometres between two points given as
// individual (lon, lat) arguments.  Used by ORAOverlayCard nearby filtering.
export function haversineKm(lon1, lat1, lon2, lat2) {
  return haversineMeters([lon1, lat1], [lon2, lat2]) / 1000;
}

// Format a distance in metres to a human-readable string in the given unit.
export function formatDistance(meters, unit) {
  if (meters === null || meters === undefined) return "";
  switch (unit) {
    case "meters": return `${Math.round(meters)} m`;
    case "yards":      return `${Math.round(meters * 1.09361)} yd`;
    case "kilometers": {
      const km = meters / 1000;
      return km < 10 ? `${km.toFixed(2)} km` : `${km.toFixed(1)} km`;
    }
    case "nautical": {
      const nm = meters / 1852;
      return nm < 10 ? `${nm.toFixed(2)} nmi` : `${nm.toFixed(1)} nmi`;
    }
    case "miles":
    default: {
      const mi = meters / 1609.344;
      return mi < 10 ? `${mi.toFixed(2)} mi` : `${mi.toFixed(1)} mi`;
    }
  }
}

// Generate a GeoJSON FeatureCollection with a Polygon circle (closed ring).
// Used as the source for the cone-of-silence-ring line layer; 64 steps
// gives a smooth boundary for the ~5 km near-range exclusion ring.
export function generateCirclePolygon(lat, lon, radiusKm, steps = 64) {
  const R  = 6371;
  const φ1 = (lat * Math.PI) / 180;
  const λ1 = (lon * Math.PI) / 180;
  const d  = radiusKm / R;
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const φ2 = Math.asin(
      Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(bearing),
    );
    const λ2 = λ1 + Math.atan2(
      Math.sin(bearing) * Math.sin(d) * Math.cos(φ1),
      Math.cos(d) - Math.sin(φ1) * Math.sin(φ2),
    );
    coords.push([(λ2 * 180) / Math.PI, (φ2 * 180) / Math.PI]);
  }
  // GeoJSON Polygon ring must be closed (first === last point); the loop at
  // i = steps wraps bearing back to 0 so coords[0] === coords[steps].
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", geometry: { type: "Polygon", coordinates: [coords] }, properties: {} }],
  };
}

// Generate a GeoJSON FeatureCollection with a single LineString approximating
// a great-circle range ring.  120 steps gives a smooth circle at any zoom.
export function generateRangeRingGeoJson(lat, lon, radiusKm, steps = 120) {
  const R   = 6371;
  const φ1  = (lat * Math.PI) / 180;
  const λ1  = (lon * Math.PI) / 180;
  const d   = radiusKm / R;
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const φ2 = Math.asin(
      Math.sin(φ1) * Math.cos(d) + Math.cos(φ1) * Math.sin(d) * Math.cos(bearing),
    );
    const λ2 = λ1 + Math.atan2(
      Math.sin(bearing) * Math.sin(d) * Math.cos(φ1),
      Math.cos(d) - Math.sin(φ1) * Math.sin(φ2),
    );
    coords.push([(λ2 * 180) / Math.PI, (φ2 * 180) / Math.PI]);
  }
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", geometry: { type: "LineString", coordinates: coords }, properties: {} }],
  };
}
